import type { ReactNode } from "react";

import { cn } from "@/utils/cn";

// Figma: https://www.figma.com/design/HJIp5P6tyeFrUd7ODqeWdM/-PROD--Fertility-V?node-id=4180-18732
// (Core DS `icon container / small`) and node 4180-18784 (`icon container / large`).

export const ICON_CONTAINER_SIZES = ["small", "large"] as const;
export const ICON_CONTAINER_TONES = ["pond", "dark-green", "neutral"] as const;

export type IconContainerSize = (typeof ICON_CONTAINER_SIZES)[number];
export type IconContainerTone = (typeof ICON_CONTAINER_TONES)[number];

type IconContainerProps = {
  /** A `ui` icon, drawn white (black on `neutral`). */
  children: ReactNode;
  size?: IconContainerSize;
  tone?: IconContainerTone;
  /** Layout only. A repeated look is a variant. */
  className?: string;
};

// The 20px and 32px frames and the large 20px icon have no Figma variable (EVY-436).
const SIZES: Record<IconContainerSize, string> = {
  small: "size-[20px] [&_svg]:size-ds-icon-small",
  large: "size-[32px] [&_svg]:size-[20px]",
};

const TONES: Record<IconContainerTone, string> = {
  pond: "bg-ds-all-colors-pond",
  "dark-green": "bg-ds-ui-elements-dark-green",
  // A muted mark, such as a comparison table's "no".
  neutral: "bg-ds-ui-elements-deactivated text-ds-ui-elements-black",
};

// Display component, no ref forwarded. See ui/README.md.
export const IconContainer = ({
  children,
  size = "small",
  tone = "pond",
  className,
}: IconContainerProps) => (
  <span
    className={cn(
      "inline-flex shrink-0 items-center justify-center rounded-ds-corner-full text-ds-ui-elements-white",
      SIZES[size],
      TONES[tone],
      className
    )}
  >
    {children}
  </span>
);
